import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Output,
	ViewEncapsulation,
} from '@angular/core';
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { UtilService } from '@service/util/util.service';
import {
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	ADD_DAYS,
	CustomerDTO,
	DATE_MDY,
	DATE_TIME,
	DATE_YMD,
	IS_EMPTY,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	Location,
	Owner,
	PROJECT_STAGE_UUID_MAP,
	ProjectDTO,
	RestService,
	PROJECT_CARRIER_MAP_STATUS,
	TITLE_CASE,
	IS_NULL_OR_UNDEFINED,
	INTEGRATION_TYPE,
} from 'cargo-chief-commons';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { BaConfigService } from '@service/baConfig/ba-config.service';
import { TOOLTIP_MSG, OFFER_RATE_CONFIRMATION } from '@domain/constants/app-variables';
import { UrlService } from '@service/url/url.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { PlanProductService } from '@service/planProducts/plan-product.service';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { environment } from '@environment/environment';
import { NotifyProcuredCarriersDialogComponent } from '../notify-procured-carriers-dialog/notify-procured-carriers-dialog.component';
import { TRAILER_TYPES_DB } from '@domain/constants/trailer-types';
import { BaseComponent } from '@shared/base/base.component';
import { PROJECT_TYPES, IS_WORKFLOW_PROJECT_TYPE } from '@domain/constants/project-types';
import { ProjectTypeDialogComponent } from '@shared/project-type-dialog/project-type-dialog.component';
import { Store } from '@ngrx/store';
import { ERROR_HANDLING } from '../../store/actions/error-handling.actions';
import { Router } from '@angular/router';
import { ProjectUtilService } from '@service/project-util/project-util.service';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { IntegrationService } from '@service/integration/integration.service';
import { ConfirmationDialogComponent } from '../../common/confirmation-dialog/confirmation-dialog.component';

@Component({
	selector: 'app-project-dialog',
	templateUrl: './project-dialog.component.html',
	styleUrls: ['./project-dialog.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ProjectDialogComponent implements OnInit {
	@Input() isEditProject: boolean = false;
	@Input() isDuplicateProject: boolean = false;
	@Input() addProjectLink: boolean = false;
	@Input() forScorecardAction: boolean = false;
	@Input() useMenuButton: boolean = false;
	@Input() addProject: boolean = false;
	@Input() noCaps: boolean = false;

	@Input() searchTruckForm: null;
	@Input() truckLaneSearchId: string = null;
	@Input() truckSearchId: string = null;
	@Input() carrierIDs: Array<string> = null;
	@Input() projectDTO: ProjectDTO = null;
	@Input() lanePricing: any = null;
	@Input() projectSource: string = '';
	@Input() fullWidth: boolean;
	@Input() usersList: Array<Owner> = [];
	@Input() addAllCarriers: boolean = false;
	@Input() selectedCustomer: CustomerDTO = null;
	@Input() originCity: string = '';
	@Input() originState: string = '';
	@Input() destCity: string = '';
	@Input() destState: string = '';
	@Input() laneId: string = null;
	@Input() disabled: boolean = false;
	@Input() textClass: string = 'primary-text';
	@Input() loadVolume: number = null;
	@Input() loadVolumeDuration: string = null;
	@Input() scorecardStartDate: string = null;
	@Input() scorecardEndDate: string = null;
	@Input() scorecardLoadsCount: number = null;
	@Input() carrierId: string = null;
	@Input() customerId: string = null;

	@Output() updatedData = new EventEmitter<string>();

	constructor(
		public dialog: MatDialog,
		public accountSettings: AccountSettingsService
	) {}

	ngOnInit() {}

	async openProjectDialog(): Promise<void> {
		const data: ProjectDialogInputData = {
			isEditProject: this.isEditProject,
			addProject: this.addProject,
			searchTruckForm: this.searchTruckForm,
			truckLaneSearchId: this.truckLaneSearchId,
			truckSearchId: this.truckSearchId,
			carrierIDs: this.carrierIDs,
			projectDTO: this.projectDTO,
			isDuplicateProject: this.isDuplicateProject,
			lanePricing: this.lanePricing,
			addProjectLink: this.addProjectLink,
			projectSource: this.projectSource,
			usersList: this.usersList,
			addAllCarriers: this.addAllCarriers,
			forScorecardAction: this.forScorecardAction,
			selectedCustomer: this.selectedCustomer,
			laneId: this.laneId,
			originCity: this.originCity,
			originState: this.originState,
			destCity: this.destCity,
			destState: this.destState,
			loadVolume: this.loadVolume,
			loadVolumeDuration: this.loadVolumeDuration,
			scorecardStartDate: this.scorecardStartDate,
			scorecardEndDate: this.scorecardEndDate,
			scorecardLoadsCount: this.scorecardLoadsCount,
			carrierId: this.carrierId,
			customerId: this.customerId,
		};

		// Capacity Search: ask user to pick Procurement vs Workflow before opening the project form
		if (
			!this.isEditProject &&
			!this.isDuplicateProject &&
			this.projectSource === 'capacity_search'
		) {
			const typeDialogRef = this.dialog.open(ProjectTypeDialogComponent, {
				panelClass: 'cargo_chief_dialog_container',
				width: '450px',
				data: { ...data, showProjectTypeSelectionDialog: true },
				disableClose: true,
			});
			typeDialogRef.afterClosed().subscribe((projectDialogRef) => {
				if (projectDialogRef) {
					this.subscribeToProjectDialogClose(projectDialogRef);
				}
			});
			return;
		}

		// Scorecard "Build Project" creates a Procurement project; otherwise default to Waterfall.
		// Edit/duplicate preserves the existing type.
		const type =
			(this.isEditProject || this.isDuplicateProject) && this.projectDTO?.type
				? this.projectDTO.type
				: this.forScorecardAction
					? PROJECT_TYPES.PROCUREMENT.value
					: PROJECT_TYPES.WATERFALL.value;
		const projectDialogRef = this.dialog.open(ProjectDialog, {
			panelClass: 'cargo_chief_dialog_container',
			width: '950px',
			data: { ...data, type },
			disableClose: true,
		});
		this.subscribeToProjectDialogClose(projectDialogRef);
	}

	subscribeToProjectDialogClose(projectDialog: MatDialogRef<ProjectDialog>) {
		projectDialog.afterClosed().subscribe((result) => {
			if (IS_NOT_NULL_OR_UNDEFINED(result)) {
				this.updatedData.emit(result);
			}
		});
	}
}

@Component({
	selector: 'app-project-dialog-open',
	templateUrl: 'modal.html',
	styleUrls: ['./project-dialog.component.scss'],
})
export class ProjectDialog extends BaseComponent implements OnInit {
	isFormInitialized = false;
	minimumDueDate = new Date();
	dueDate = new UntypedFormControl(new Date(ADD_DAYS(DATE_TIME(new Date()), 1)));
	loadVolumeDuration = ['Day', 'Week', 'Month', 'Year'];
	isRequestInProgress: boolean = false;
	specifyDays: boolean = false;
	grossMargin: any = null;
	grossMarginChanged: Subject<any> = new Subject<any>();

	projectDetails: UntypedFormGroup;
	TARGET_MARGIN: number = null;
	TOOLTIP_MSG = TOOLTIP_MSG;
	PROJECT_STAGES: Array<any> = [];
	origin: Location = null;
	destination: Location = null;
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	notesExpanded: boolean = false;
	IS_TEAMS_CAPACITY_ENABLED: boolean = false;
	usersList: Array<Owner> = [];
	trailerType: string = '';
	PROJECT_TYPES = PROJECT_TYPES;
	isWaterfallOrDedicatedLaneProject: boolean = false;
	isMcleodIntegrationEnabled: boolean = false;

	constructor(
		public dialogRef: MatDialogRef<ProjectDialog>,
		@Inject(MAT_DIALOG_DATA) public data: ProjectDialogInputData,
		private formBuilder: UntypedFormBuilder,
		private cargochief: Cargochief,
		public utilService: UtilService,
		private urlService: UrlService,
		private restService: RestService,
		private segmentWrapper: SegmentWrapperService,
		public planProductService: PlanProductService,
		private projectHelperService: ProjectHelperService,
		public accountSettings: AccountSettingsService,
		private store: Store,
		private router: Router,
		private cdRef: ChangeDetectorRef,
		private projectUtilService: ProjectUtilService,
		public readonly baConfigService: BaConfigService,
		private integrationService: IntegrationService,
		private dialog: MatDialog
	) {
		super();
		this.grossMarginChanged
			.pipe(debounceTime(250), takeUntil(this.isDestroyed$))
			.subscribe((model) => {
				this.calculateMarginChange();
			});
		this.IS_TEAMS_CAPACITY_ENABLED =
			environment.TEAMS_CAPACITY && this.accountSettings.TEAMS_CAPACITY;
		if (this.data.usersList) {
			this.usersList = this.data.usersList.map((user) => Object.assign({}, user));
		}
	}

	async ngOnInit() {
		this.TARGET_MARGIN = this.accountSettings.TARGET_MARGIN;
		this.PROJECT_STAGES = this.projectHelperService.getProjectStages();
		this.grossMargin = this.TARGET_MARGIN;
		this.isMcleodIntegrationEnabled = this.integrationService.isIntegrationEnabled(
			INTEGRATION_TYPE.MCLEOD
		);
		await this.populateOrigin();
		await this.populateDestination();
		this.setUpForm();
		if (this.data.forScorecardAction) {
			await this.getEquipment();
		}
	}

	setUpForm() {
		this.projectDetails = this.formBuilder.group({
			userId: this.cargochief.USER_ID,
			originCity: this.data.originCity,
			originState: this.data.originState,
			destCity: this.data.destCity,
			destState: this.data.destState,
			commodity: [null],
			weight: [null],
			dueDate: [''],
			truckLaneSearchId: this.data.truckLaneSearchId,
			truckSearchId: this.data.truckSearchId,
			deleted: false,
			equipmentType: '',
			monday: [false],
			tuesday: [false],
			wednesday: [false],
			thursday: [false],
			friday: [false],
			saturday: [false],
			sunday: [false],
			addAllCarriers: [this.data.addAllCarriers],
			name: ['', Validators.required],
			originZip: IS_NOT_NULL_OR_UNDEFINED(this.origin) ? this.origin.zip : '',
			destZip: IS_NOT_NULL_OR_UNDEFINED(this.destination) ? this.destination.zip : '',
			originLat: IS_NOT_NULL_OR_UNDEFINED(this.origin) ? this.origin.latitude : null,
			originLng: IS_NOT_NULL_OR_UNDEFINED(this.origin) ? this.origin.longitude : null,
			destLat: IS_NOT_NULL_OR_UNDEFINED(this.destination) ? this.destination.latitude : null,
			destLng: IS_NOT_NULL_OR_UNDEFINED(this.destination) ? this.destination.longitude : null,
			originCountry: IS_NOT_NULL_OR_UNDEFINED(this.origin) ? this.origin.country : null,
			destCountry: IS_NOT_NULL_OR_UNDEFINED(this.destination) ? this.destination.country : null,
			minRate: null,
			maxRate: null,
			maxBuy: null,
			offerRate: null,
			packaging: '',
			trailerRequirements: '',
			shipperNotes: '',
			receiverNotes: '',
			accountId: this.cargochief.ACCOUNT_ID,
			value: null,
			loadVolume: this.data.loadVolume,
			loadVolumeDuration: this.data.loadVolumeDuration,
			assigneeId: null,
			customerDTO: this.data.selectedCustomer,
			stageId: PROJECT_STAGE_UUID_MAP.NEW,
			taskCount: 0,
			carrierCount: 0,
			uploadCount: 0,
			laneId: this.data.laneId,
			requireTeams: false,
			type: this.data.type,
			notifyProcuredCarriers: this.data.notifyProcuredCarriers,
			inviteUngroupedCarriers: this.data.type == PROJECT_TYPES.WATERFALL.value ? true : false,
			revenueCode: '',
			ungroupedCarriersDelay: 30,
			sendToOutOfNetworkCarriers: false,
			sendToOutOfNetworkCarriersDelay: [{ value: null, disabled: true }],
			enabled: true,
			assigneeIsDispatcher: false,
		});
		this.projectDetails.get('originCity').setValidators([]);
		this.projectDetails.get('originState').setValidators([]);
		this.projectDetails.get('destCity').setValidators([]);
		this.projectDetails.get('destState').setValidators([]);
		this.projectDetails.get('equipmentType').setValidators([]);
		this.projectDetails.get('loadVolume').setValidators([]);
		this.projectDetails.get('loadVolumeDuration').setValidators([]);
		this.projectDetails.get('revenueCode').setValidators([]);
		this.projectDetails.get('customerDTO').setValidators([]);

		if (this.origin && this.destination) {
			const trailerTypes = this.accountSettings.SEARCHABLE_TRAILER_TYPES;
			const defaultTrailerType = trailerTypes
				.reduce((accumulator, value) => accumulator.concat(value.value.toLowerCase()), [])
				.indexOf(TRAILER_TYPES_DB.VAN.toLowerCase());
			const trailerType = trailerTypes[defaultTrailerType > -1 ? defaultTrailerType : 0].dbVal;
			this.projectDetails.get('equipmentType').setValue(trailerType);
			this.setProjectName();
		}

		if (IS_NOT_NULL_OR_UNDEFINED(this.data.searchTruckForm)) {
			this.projectDetails.get('equipmentType').setValue(this.data.searchTruckForm.trailerType);

			if (IS_NOT_EMPTY(this.data.searchTruckForm.origin.city)) {
				this.projectDetails.get('originCity').setValue(this.data.searchTruckForm.origin.city);
				this.projectDetails.get('originState').setValue(this.data.searchTruckForm.origin.state);
				this.projectDetails.get('originZip').setValue(this.data.searchTruckForm.origin.zip);
				this.projectDetails.get('originLat').setValue(this.data.searchTruckForm.origin.latitude);
				this.projectDetails.get('originLng').setValue(this.data.searchTruckForm.origin.longitude);
				this.projectDetails
					.get('originCountry')
					.setValue(this.data.searchTruckForm.origin.country);
				this.projectDetails.get('originCity').setValidators([Validators.required]);
				this.projectDetails.get('originState').setValidators([Validators.required]);
				this.projectDetails.get('equipmentType').setValidators([Validators.required]);
			}

			if (IS_NOT_EMPTY(this.data.searchTruckForm.destination.city)) {
				this.projectDetails.get('destCity').setValue(this.data.searchTruckForm.destination.city);
				this.projectDetails.get('destState').setValue(this.data.searchTruckForm.destination.state);
				this.projectDetails.get('destZip').setValue(this.data.searchTruckForm.destination.zip);
				this.projectDetails.get('destLat').setValue(this.data.searchTruckForm.destination.latitude);
				this.projectDetails
					.get('destLng')
					.setValue(this.data.searchTruckForm.destination.longitude);
				this.projectDetails
					.get('destCountry')
					.setValue(this.data.searchTruckForm.destination.country);
				this.projectDetails.get('destCity').setValidators([Validators.required]);
				this.projectDetails.get('destState').setValidators([Validators.required]);
				this.projectDetails.get('equipmentType').setValidators([Validators.required]);
			}

			if (
				!this.utilService.isNullOrUndefined(this.data.lanePricing) &&
				!this.utilService.isNullOrUndefined(this.data.lanePricing.allInCostHigh) &&
				!this.utilService.isNullOrUndefined(this.data.lanePricing.allInCostLow)
			) {
				this.projectDetails.get('maxRate').setValue(Math.round(this.data.lanePricing.allInCost));
				this.projectDetails.get('minRate').setValue(Math.round(this.data.lanePricing.allInCostLow));
			}

			this.setProjectName();
		}

		if (
			(this.data.isEditProject || this.data.isDuplicateProject) &&
			!this.utilService.isNullOrUndefined(this.data.projectDTO)
		) {
			this.updateEditProjectForm();
		}

		this.customerRateChanged();
		this.isFormInitialized = true;
		this.subscribeToFormChange();
		this.isWaterfallOrDedicatedLaneProject = IS_WORKFLOW_PROJECT_TYPE(this.data.type);
		if (
			this.isWaterfallOrDedicatedLaneProject &&
			this.accountSettings.ENABLE_OUT_OF_NETWORK_CARRIERS
		) {
			this.setOutOfNetworkFormField();
		}
	}

	subscribeToFormChange() {
		this.projectDetails.valueChanges
			.pipe(takeUntil(this.isDestroyed$), debounceTime(250))
			.subscribe((formValue) => {
				if (
					IS_NOT_EMPTY(formValue.originCity) &&
					IS_NOT_EMPTY(formValue.destCity) &&
					IS_NOT_EMPTY(formValue.equipmentType)
				) {
					this.projectDetails.get('customerDTO').setValidators([]);
					this.projectDetails.get('revenueCode').setValidators([]);
				}

				if (
					(IS_NOT_NULL_OR_UNDEFINED(formValue.customerDTO) ||
						IS_NOT_NULL_OR_UNDEFINED(formValue.revenueCode)) &&
					IS_EMPTY(formValue.originCity) &&
					IS_EMPTY(formValue.destCity) &&
					IS_EMPTY(formValue.equipmentType)
				) {
					this.projectDetails.get('originCity').setValidators([]);
					this.projectDetails.get('originState').setValidators([]);
					this.projectDetails.get('destCity').setValidators([]);
					this.projectDetails.get('destState').setValidators([]);
					this.projectDetails.get('equipmentType').setValidators([]);
				}

				const controlsToUpdate = [
					'customerDTO',
					'revenueCode',
					'originCity',
					'originState',
					'destCity',
					'destState',
					'equipmentType',
					'loadVolume',
					'loadVolumeDuration',
				];
				controlsToUpdate.forEach((control) =>
					this.projectDetails.get(control).updateValueAndValidity()
				);
			});
	}

	updateEditProjectForm() {
		this.projectDetails.get('originCity').setValue(this.data.projectDTO.originCity);
		this.projectDetails.get('originState').setValue(this.data.projectDTO.originState);
		this.projectDetails.get('destCity').setValue(this.data.projectDTO.destCity);
		this.projectDetails.get('destState').setValue(this.data.projectDTO.destState);
		this.projectDetails.get('equipmentType').setValue(this.data.projectDTO.equipmentType);
		this.projectDetails.get('originZip').setValue(this.data.projectDTO.originZip);
		this.projectDetails.get('originLat').setValue(this.data.projectDTO.originLat);
		this.projectDetails.get('originLng').setValue(this.data.projectDTO.originLng);
		this.projectDetails.get('destZip').setValue(this.data.projectDTO.destZip);
		this.projectDetails.get('destLat').setValue(this.data.projectDTO.destLat);
		this.projectDetails.get('destLng').setValue(this.data.projectDTO.destLng);
		this.projectDetails.get('originCountry').setValue(this.data.projectDTO.originCountry);
		this.projectDetails.get('destCountry').setValue(this.data.projectDTO.destCountry);
		this.projectDetails.get('name').setValue(this.data.projectDTO.name);

		this.projectDetails
			.get('minRate')
			.setValue(
				!this.utilService.isNullOrUndefined(this.data.projectDTO.minRate)
					? this.data.projectDTO.minRate / 100
					: null
			);
		this.projectDetails
			.get('maxRate')
			.setValue(
				!this.utilService.isNullOrUndefined(this.data.projectDTO.maxRate)
					? this.data.projectDTO.maxRate / 100
					: null
			);
		this.projectDetails
			.get('maxBuy')
			.setValue(
				!this.utilService.isNullOrUndefined(this.data.projectDTO.maxBuy)
					? this.data.projectDTO.maxBuy / 100
					: null
			);
		this.projectDetails
			.get('offerRate')
			.setValue(
				!this.utilService.isNullOrUndefined(this.data.projectDTO.offerRate)
					? this.data.projectDTO.offerRate / 100
					: null
			);
		this.projectDetails
			.get('weight')
			.setValue(
				!this.utilService.isNullOrUndefined(this.data.projectDTO.weight)
					? this.data.projectDTO.weight
					: null
			);
		this.projectDetails.get('packaging').setValue(this.data.projectDTO.packaging);
		this.projectDetails
			.get('trailerRequirements')
			.setValue(this.data.projectDTO.trailerRequirements);
		this.projectDetails.get('shipperNotes').setValue(this.data.projectDTO.shipperNotes);
		this.projectDetails.get('receiverNotes').setValue(this.data.projectDTO.receiverNotes);
		this.projectDetails.get('commodity').setValue(this.data.projectDTO.commodity);
		this.projectDetails.get('loadVolumeDuration').setValue(this.data.projectDTO.loadVolumeDuration);
		this.projectDetails.get('monday').setValue(this.data.projectDTO.monday);
		this.projectDetails.get('tuesday').setValue(this.data.projectDTO.tuesday);
		this.projectDetails.get('wednesday').setValue(this.data.projectDTO.wednesday);
		this.projectDetails.get('thursday').setValue(this.data.projectDTO.thursday);
		this.projectDetails.get('friday').setValue(this.data.projectDTO.friday);
		this.projectDetails.get('saturday').setValue(this.data.projectDTO.saturday);
		this.projectDetails.get('sunday').setValue(this.data.projectDTO.sunday);

		if (
			this.data.projectDTO.monday ||
			this.data.projectDTO.tuesday ||
			this.data.projectDTO.wednesday ||
			this.data.projectDTO.thursday ||
			this.data.projectDTO.friday ||
			this.data.projectDTO.saturday ||
			this.data.projectDTO.sunday
		) {
			this.specifyDays = true;
		}
		this.projectDetails
			.get('value')
			.setValue(
				!this.utilService.isNullOrUndefined(this.data.projectDTO.value)
					? this.data.projectDTO.value / 100
					: null
			);
		this.projectDetails
			.get('loadVolume')
			.setValue(
				!this.utilService.isNullOrUndefined(this.data.projectDTO.loadVolume)
					? this.data.projectDTO.loadVolume
					: null
			);

		if (this.data.isEditProject) {
			this.dueDate.setValue(new Date(DATE_MDY(this.data.projectDTO.dueDate)));
			this.projectDetails.controls['name'].disable();
		}
		if (this.data.isDuplicateProject) {
			this.dueDate.setValue(new Date());
		}

		if (!this.utilService.isNullOrUndefined(this.data.projectDTO.customerDTO)) {
			this.projectDetails.get('customerDTO').setValue(this.data.projectDTO.customerDTO);
		}
		this.projectDetails.get('taskCount').setValue(this.data.projectDTO.taskCount);
		this.projectDetails.get('carrierCount').setValue(this.data.projectDTO.carrierCount);
		this.projectDetails.get('uploadCount').setValue(this.data.projectDTO.uploadCount);
		this.projectDetails.get('requireTeams').setValue(this.data.projectDTO.requireTeams);

		// Preserve out-of-network settings when editing/duplicating
		if (this.data.projectDTO.sendToOutOfNetworkCarriers) {
			this.projectDetails.get('sendToOutOfNetworkCarriers').setValue(true);
			this.projectDetails.get('sendToOutOfNetworkCarriersDelay').enable();
			this.projectDetails
				.get('sendToOutOfNetworkCarriersDelay')
				.setValue(this.data.projectDTO.sendToOutOfNetworkCarriersDelay);
			this.projectDetails
				.get('sendToOutOfNetworkCarriersDelay')
				.setValidators([Validators.required, Validators.min(0)]);
		}
	}

	async addProject() {
		if (this.projectDetails.valid) {
			// Check if offer rate is being set to 0
			const offerRate = this.projectDetails.get('offerRate').value;
			if (offerRate === 0) {
				const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
					width: '500px',
					data: {
						title: OFFER_RATE_CONFIRMATION.TITLE,
						body: OFFER_RATE_CONFIRMATION.MESSAGE,
						cancelButtonText: OFFER_RATE_CONFIRMATION.CANCEL,
						ConfirmButtonText: OFFER_RATE_CONFIRMATION.CONFIRM,
					},
					disableClose: true,
				});

				const result = await dialogRef.afterClosed().toPromise();
				if (!result) {
					// User cancelled, don't proceed with project creation
					return;
				}
			}

			this.isRequestInProgress = true;
			this.buildProjectDTO();
			const url = this.urlService.addProject();
			let payload = Object.assign({}, this.projectDetails.value);
			payload.minRate = Number(payload.minRate) * 100;
			payload.maxRate = Number(payload.maxRate) * 100;
			payload.maxBuy = Number(payload.maxBuy) * 100;
			payload.offerRate =
				payload.offerRate === '' || this.utilService.isNullOrUndefined(payload.offerRate)
					? null
					: Number(payload.offerRate) * 100;
			payload.value = Number(payload.value) * 100;
			this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.user_created_project, {
				project: payload,
				added_matched_carriers: this.projectDetails.controls['addAllCarriers'].value,
				project_source: this.data.projectSource,
			});
			this.restService
				.POST(url, payload)
				.pipe(takeUntil(this.isDestroyed$))
				.subscribe(
					(result: any) => {
						this.utilService.showSnackbar(
							'Project added successfully.',
							SNACKBAR.SUCCESS,
							SNACKBAR.ACTION
						);
						if (
							this.data.projectSource == 'projects_page' ||
							this.data.projectSource == 'procurement_page'
						) {
							this.closeDialog(result.data);
							this.isRequestInProgress = false;
							this.router.navigate([`projects/project_details/${result.data.id}`]);
							return;
						}
						if (
							this.projectDetails.controls['addAllCarriers'].value &&
							!this.utilService.isNullOrUndefined(this.data.carrierIDs)
						) {
							this.utilService.showSnackbar(
								'Adding carriers now.',
								SNACKBAR.SUCCESS,
								SNACKBAR.ACTION
							);
							this.addCarriersToProject(result.data);
						} else {
							this.handleProjectCreationSuccess(result.data);
						}
					},
					(error) => {
						this.store.dispatch(
							ERROR_HANDLING.HANDLE_PROJECT_UPDATE_ERROR({
								error,
								forAdd: true,
								defaultErrorMessage: 'Unable to add project now.',
							})
						);
						this.isRequestInProgress = false;
					}
				);
		}
	}

	updateProject() {
		if (this.projectDetails.valid) {
			this.isRequestInProgress = true;
			this.buildProjectDTO();
			const url = this.urlService.addProject();
			this.setProjectDTOWithFormData();
			this.restService
				.PUT(url, this.data.projectDTO)
				.pipe(takeUntil(this.isDestroyed$))
				.subscribe(
					(result: any) => {
						this.utilService.showSnackbar(
							'Project updated successfully.',
							SNACKBAR.SUCCESS,
							SNACKBAR.ACTION
						);
						this.closeDialog(result.data);
						this.isRequestInProgress = false;
					},
					(error) => {
						this.store.dispatch(
							ERROR_HANDLING.HANDLE_PROJECT_UPDATE_ERROR({
								error,
								forAdd: false,
								defaultErrorMessage: 'Unable to update project now.',
							})
						);
						this.isRequestInProgress = false;
					}
				);
		}
	}

	buildProjectDTO() {
		this.projectDetails.get('dueDate').setValue(DATE_YMD(this.dueDate.value));
	}

	addCarriersToProject(projectDTO: ProjectDTO) {
		const payload = {
			carrierIDs: this.data.carrierIDs,
			status: PROJECT_CARRIER_MAP_STATUS.PROCURED,
		};
		const url = this.urlService.addProjectCarrierMap(projectDTO.id);
		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(result) => {
					result = `${result.length} carriers added to project`;
					this.utilService.showSnackbar(result, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
					projectDTO.carrierCount += result.length;
					this.closeDialog(projectDTO);
					this.isRequestInProgress = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to add carriers to created project now.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.isRequestInProgress = false;
				}
			);
	}

	enableAddAllCarriers() {
		return (
			!this.utilService.isNullOrUndefined(this.data.truckLaneSearchId) ||
			!this.utilService.isNullOrUndefined(this.data.truckSearchId) ||
			this.data.addAllCarriers
		);
	}

	syncSearchData(event: any) {
		if (IS_NOT_NULL_OR_UNDEFINED(event.origin)) {
			// For workflows, allow state-only (city can be null)
			// For other projects, only set if city exists
			if (IS_NOT_EMPTY(event.origin.city) || this.isWaterfallOrDedicatedLaneProject) {
				this.projectDetails.get('originCity').setValue(event.origin.city);
				this.projectDetails.get('originState').setValue(event.origin.state);
				this.projectDetails.get('originZip').setValue(event.origin.zip);
				this.projectDetails.get('originLat').setValue(event.origin.latitude);
				this.projectDetails.get('originLng').setValue(event.origin.longitude);
				this.projectDetails.get('originCountry').setValue(event.origin.country);
			} else if (IS_EMPTY(event.origin.city)) {
				this.utilService.showSnackbar(
					'Please select a city for origin.',
					SNACKBAR.WARNING,
					SNACKBAR.ACTION
				);
			}
		}
		if (IS_NOT_NULL_OR_UNDEFINED(event.destination)) {
			// For workflows, allow state-only (city can be null)
			// For other projects, only set if city exists
			if (IS_NOT_EMPTY(event.destination.city) || this.isWaterfallOrDedicatedLaneProject) {
				this.projectDetails.get('destCity').setValue(event.destination.city);
				this.projectDetails.get('destState').setValue(event.destination.state);
				this.projectDetails.get('destZip').setValue(event.destination.zip);
				this.projectDetails.get('destLat').setValue(event.destination.latitude);
				this.projectDetails.get('destLng').setValue(event.destination.longitude);
				this.projectDetails.get('destCountry').setValue(event.destination.country);
			} else if (IS_EMPTY(event.destination.city)) {
				this.utilService.showSnackbar(
					'Please select a city for destination.',
					SNACKBAR.WARNING,
					SNACKBAR.ACTION
				);
			}
		}
		if (IS_NULL_OR_UNDEFINED(event.trailerType)) {
			this.projectDetails.get('equipmentType').setValue(null);
		} else {
			this.projectDetails.get('equipmentType').setValue(event.trailerType.join(','));
		}
		if (!this.data.isEditProject) {
			this.setProjectName();
		}
	}

	setSpecifyDays() {
		this.specifyDays = !this.specifyDays;
		this.projectDetails.get('monday').setValue(false);
		this.projectDetails.get('tuesday').setValue(false);
		this.projectDetails.get('wednesday').setValue(false);
		this.projectDetails.get('thursday').setValue(false);
		this.projectDetails.get('friday').setValue(false);
		this.projectDetails.get('saturday').setValue(false);
		this.projectDetails.get('sunday').setValue(false);
	}

	setProjectName() {
		let name = '',
			originCityExist = false,
			originStateExist = false,
			destCityExist = false,
			destStateExist = false,
			projectFormValue = this.projectDetails.value;
		if (IS_NOT_EMPTY(projectFormValue.originCity)) {
			name += projectFormValue.originCity;
			originCityExist = true;
		}
		if (IS_NOT_EMPTY(projectFormValue.originState)) {
			name += originCityExist ? ', ' : '';
			name += projectFormValue.originState;
			originStateExist = true;
		}
		// Add arrow before destination if we have origin and destination
		const hasDestination =
			IS_NOT_EMPTY(projectFormValue.destCity) || IS_NOT_EMPTY(projectFormValue.destState);
		if (hasDestination && (originCityExist || originStateExist)) {
			name += ' → ';
		}
		if (IS_NOT_EMPTY(projectFormValue.destCity)) {
			name += projectFormValue.destCity;
			destCityExist = true;
		}
		if (IS_NOT_EMPTY(projectFormValue.destState)) {
			name += destCityExist ? ', ' : '';
			name += projectFormValue.destState;
			destStateExist = true;
		}
		if (IS_NOT_EMPTY(projectFormValue.equipmentType)) {
			name += originCityExist || originStateExist || destCityExist || destStateExist ? ' ' : '';
			name +=
				'(' +
				projectFormValue.equipmentType
					.split(',')
					.map((equip) => equip[0])
					.join(',') +
				')';
		}
		const customer = this.projectDetails.get('customerDTO').value;
		const hasCustomer = IS_NOT_NULL_OR_UNDEFINED(customer);
		if (hasCustomer) {
			const customerName = TITLE_CASE(customer.name);
			name = [customerName, name].filter(Boolean).join(': ');
		}
		const revenueCode = this.projectDetails.get('revenueCode').value;
		if (IS_NOT_NULL_OR_UNDEFINED(revenueCode) && IS_EMPTY(name)) {
			name = [revenueCode, name].filter(Boolean).join(': ');
		}
		if (IS_NOT_EMPTY(name)) {
			this.projectDetails.get('name').setValue(name);
			this.cdRef.detectChanges();
		}
	}

	setProjectDTOWithFormData() {
		this.data.projectDTO.originCity = this.projectDetails.get('originCity').value;
		this.data.projectDTO.originState = this.projectDetails.get('originState').value;
		this.data.projectDTO.destCity = this.projectDetails.get('destCity').value;
		this.data.projectDTO.destState = this.projectDetails.get('destState').value;
		this.data.projectDTO.equipmentType = this.projectDetails.get('equipmentType').value;
		this.data.projectDTO.originZip = this.projectDetails.get('originZip').value;
		this.data.projectDTO.originLat = this.projectDetails.get('originLat').value;
		this.data.projectDTO.originLng = this.projectDetails.get('originLng').value;
		this.data.projectDTO.destZip = this.projectDetails.get('destZip').value;
		this.data.projectDTO.destLat = this.projectDetails.get('destLat').value;
		this.data.projectDTO.destLng = this.projectDetails.get('destLng').value;
		this.data.projectDTO.originCountry = this.projectDetails.get('originCountry').value;
		this.data.projectDTO.destCountry = this.projectDetails.get('destCountry').value;
		this.data.projectDTO.dueDate = DATE_YMD(this.dueDate.value);

		this.data.projectDTO.minRate = !this.utilService.isNullOrUndefined(
			this.projectDetails.get('minRate').value
		)
			? this.projectDetails.get('minRate').value * 100
			: null;
		this.data.projectDTO.maxRate = !this.utilService.isNullOrUndefined(
			this.projectDetails.get('maxRate').value
		)
			? this.projectDetails.get('maxRate').value * 100
			: null;
		this.data.projectDTO.maxBuy = !this.utilService.isNullOrUndefined(
			this.projectDetails.get('maxBuy').value
		)
			? this.projectDetails.get('maxBuy').value * 100
			: null;
		this.data.projectDTO.offerRate =
			this.projectDetails.get('offerRate').value === '' ||
			this.utilService.isNullOrUndefined(this.projectDetails.get('offerRate').value)
				? null
				: this.projectDetails.get('offerRate').value * 100;
		this.data.projectDTO.weight = !this.utilService.isNullOrUndefined(
			this.projectDetails.get('weight').value
		)
			? this.projectDetails.get('weight').value
			: null;
		this.data.projectDTO.packaging = this.projectDetails.get('packaging').value;
		this.data.projectDTO.trailerRequirements = this.projectDetails.get('trailerRequirements').value;
		this.data.projectDTO.shipperNotes = this.projectDetails.get('shipperNotes').value;
		this.data.projectDTO.receiverNotes = this.projectDetails.get('receiverNotes').value;

		this.data.projectDTO.monday = this.projectDetails.get('monday').value;
		this.data.projectDTO.tuesday = this.projectDetails.get('tuesday').value;
		this.data.projectDTO.wednesday = this.projectDetails.get('wednesday').value;
		this.data.projectDTO.thursday = this.projectDetails.get('thursday').value;
		this.data.projectDTO.friday = this.projectDetails.get('friday').value;
		this.data.projectDTO.saturday = this.projectDetails.get('saturday').value;
		this.data.projectDTO.sunday = this.projectDetails.get('sunday').value;

		this.data.projectDTO.commodity = this.projectDetails.get('commodity').value;
		this.data.projectDTO.loadVolumeDuration = this.projectDetails.get('loadVolumeDuration').value;

		this.data.projectDTO.value = !this.utilService.isNullOrUndefined(
			this.projectDetails.get('value').value
		)
			? this.projectDetails.get('value').value * 100
			: null;
		this.data.projectDTO.loadVolume = !this.utilService.isNullOrUndefined(
			this.projectDetails.get('loadVolume').value
		)
			? this.projectDetails.get('loadVolume').value
			: null;
		this.data.projectDTO.customerDTO = this.projectDetails.get('customerDTO').value;
		this.data.projectDTO.requireTeams = this.projectDetails.get('requireTeams').value;
		this.data.projectDTO.ungroupedCarriersDelay =
			this.projectDetails.get('ungroupedCarriersDelay').value;

		// Out-of-network settings
		this.data.projectDTO.sendToOutOfNetworkCarriers =
			this.projectDetails.get('sendToOutOfNetworkCarriers').value ?? false;
		const delayValue = this.projectDetails.get('sendToOutOfNetworkCarriersDelay').value;
		this.data.projectDTO.sendToOutOfNetworkCarriersDelay =
			!this.utilService.isNullOrUndefined(delayValue) ? Number(delayValue) : null;
	}

	setAssignee(user: Owner) {
		if (!this.utilService.isNullOrUndefined(user)) {
			this.projectDetails.get('assigneeId').setValue(user.userId);
		} else {
			this.projectDetails.get('assigneeId').setValue(null);
		}
	}

	isGrossMargin(): boolean {
		const minRate = this.projectDetails.get('minRate').value,
			maxRate = this.projectDetails.get('maxRate').value;
		if (
			(this.utilService.isNullOrUndefined(minRate) || minRate.length == 0) &&
			(this.utilService.isNullOrUndefined(maxRate) || maxRate.length == 0) &&
			this.utilService.isNullOrUndefined(this.TARGET_MARGIN)
		) {
			this.grossMargin = null;
			return true;
		}
		return false;
	}

	maxBuyChanged() {
		const maxRate = this.projectDetails.get('maxRate').value,
			minRate = this.projectDetails.get('minRate').value;
		const currentMargin = this.grossMargin;

		if (this.isGrossMarginValid(currentMargin) && this.isValidRateInput(minRate)) {
			this.projectDetails
				.get('maxRate')
				.setValue(Math.round((Number(minRate) * 100) / (100 - this.grossMargin)));
		} else if (
			!this.isGrossMarginValid(currentMargin) &&
			this.isValidRateInput(minRate) &&
			this.isValidRateInput(maxRate)
		) {
			const calculatedMargin = ((Number(maxRate) - Number(minRate)) / Number(maxRate)) * 100;

			if (calculatedMargin > 0) {
				this.grossMargin = calculatedMargin.toFixed(2);
			}
		}
	}

	customerRateChanged() {
		const maxRate = this.projectDetails.get('maxRate').value,
			minRate = this.projectDetails.get('minRate').value;
		const currentMargin = this.grossMargin;

		if (this.isGrossMarginValid(currentMargin) && this.isValidRateInput(maxRate)) {
			this.projectDetails.controls['minRate'].setValue(
				Math.round(((100 - this.grossMargin) * Number(maxRate)) / 100)
			);
		} else if (
			!this.isGrossMarginValid(currentMargin) &&
			this.isValidRateInput(minRate) &&
			this.isValidRateInput(maxRate)
		) {
			const calculatedMargin = ((Number(maxRate) - Number(minRate)) / Number(maxRate)) * 100;

			if (calculatedMargin > 0) {
				this.grossMargin = calculatedMargin.toFixed(2);
			}
		}
	}

	calculateMarginChange() {
		const minRate = this.projectDetails.get('minRate').value,
			maxRate = this.projectDetails.get('maxRate').value;

		if (
			(this.isGrossMarginValid(this.grossMargin) &&
				this.isValidRateInput(minRate) &&
				this.isValidRateInput(maxRate)) ||
			(this.isGrossMarginValid(this.grossMargin) && this.isValidRateInput(maxRate))
		) {
			this.projectDetails.controls['minRate'].setValue(
				Math.round(((100 - this.grossMargin) * Number(maxRate)) / 100)
			);
		} else if (this.isGrossMarginValid(this.grossMargin) && this.isValidRateInput(minRate)) {
			this.projectDetails
				.get('maxRate')
				.setValue(Math.round((Number(minRate) * 100) / (100 - Number(this.grossMargin))));
		}
	}

	isGrossMarginValid(margin: any): boolean {
		return !this.utilService.isNullOrUndefined(this.grossMargin) && this.grossMargin >= 0;
	}

	isValidRateInput(inputNumber: any): boolean {
		return !this.utilService.isNullOrUndefined(inputNumber) && inputNumber.toString().length > 0;
	}

	onMarginChange(marginPerccent: any) {
		this.grossMarginChanged.next(marginPerccent);
	}

	setCustomer(customer: CustomerDTO) {
		if (
			!this.utilService.isNullOrUndefined(customer) &&
			!this.utilService.isNullOrUndefined(customer.name)
		) {
			customer.name = customer.name.toLowerCase();
		}
		this.projectDetails.get('customerDTO').setValue(customer);
		this.setProjectName();
	}

	async handleProjectCreationSuccess(projectData: any) {
		// Only show notify dialog for Workflows page - Projects page handles this in ProjectTypeDialogComponent
		const isFromWorkflows =
			this.data?.allowedTypes &&
			Array.isArray(this.data.allowedTypes) &&
			this.data.allowedTypes.length === 2 &&
			this.data.allowedTypes.includes('WATERFALL') &&
			this.data.allowedTypes.includes('DEDICATED_LANE');

		// Capacity-search flow handles notify outside the form (see ProjectsTabContent.projectAddedOrUpdated)
		// to avoid the form being visible behind the notify dialog and to sequence it before AddCarriersDialog.
		const shouldShowNotifyDialog =
			(isFromWorkflows || this.data?.projectSource === 'workflows_page') &&
			environment.FEATURE_FLAGS.PROCURED_CARRIERS_EMAIL &&
			projectData.type &&
			IS_WORKFLOW_PROJECT_TYPE(projectData.type);

		// Resolve notify preference BEFORE closing the project form, so the parent's
		// post-close chain (e.g. AddCarriersDialog → navigate on capacity-search) does
		// not race the notify dialog.
		if (shouldShowNotifyDialog) {
			const notifyCarriersDialogResponse = await this.dialog
				.open(NotifyProcuredCarriersDialogComponent, {
					panelClass: 'cargo_chief_dialog_container',
					width: '450px',
					disableClose: true,
				})
				.afterClosed()
				.toPromise();

			if (
				notifyCarriersDialogResponse &&
				notifyCarriersDialogResponse.notifyProcuredCarriers !==
					projectData.notifyProcuredCarriers
			) {
				const updatedProject = {
					...projectData,
					notifyProcuredCarriers: notifyCarriersDialogResponse.notifyProcuredCarriers,
				};
				try {
					const updateResult: any = await this.restService
						.PUT(this.urlService.addProject(), updatedProject)
						.toPromise();
					projectData = updateResult?.data ?? updatedProject;
				} catch (e) {
					this.utilService.showSnackbar(
						'Unable to save notification preference.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			}
		}

		this.closeDialog(projectData);
		this.isRequestInProgress = false;

		if (shouldShowNotifyDialog && this.data?.refreshAction) {
			this.store.dispatch(this.data.refreshAction);
		}
	}

	closeDialog(data: any): void {
		this.dialogRef.close(data);
	}

	async populateOrigin() {
		if (IS_NOT_EMPTY(this.data.originCity) && IS_NOT_EMPTY(this.data.originState)) {
			const { data } = await this.getLocation(this.data.originCity, this.data.originState);
			this.origin = data;
		}
	}

	async populateDestination() {
		if (IS_NOT_EMPTY(this.data.destCity) && IS_NOT_EMPTY(this.data.destState)) {
			const { data } = await this.getLocation(this.data.destCity, this.data.destState);
			this.destination = data;
		}
	}

	getLocation(city: string, state: string): Promise<any> {
		const url = this.urlService.geocode({ city, state });
		return this.restService.GET(url).pipe(takeUntil(this.isDestroyed$)).toPromise();
	}

	getFormRequiredFields(): string {
		if (this.projectDetails.invalid) {
			const controls = this.projectDetails.controls;
			let invalid = [],
				msg;
			for (const name in controls) {
				if (controls[name].invalid) {
					switch (name) {
						case 'originCity':
							invalid.push('Origin');
							break;
						case 'destCity':
							invalid.push('Destination');
							break;
						case 'name':
							invalid.push('Project Name');
							break;
						case 'loadVolumeDuration':
							invalid.push('Duration');
							break;
						case 'loadVolume':
							invalid.push('Load Count');
							break;
					}
				}
			}
			if (invalid.length > 1) {
				invalid.splice(invalid.length - 1, 0, 'and');
				msg = `* ${invalid.join(', ')} are required`;
				msg = msg.replace(/,(?=[^,]*$)/, '');
			} else if (invalid.length == 1) {
				msg = `* ${invalid.join(', ')} is required`;
			} else {
				msg = `* ${invalid.join(', ')} are required`;
			}
			return msg;
		}
		return '';
	}

	isSearchTruckFormDataAvailable(): boolean {
		return (
			IS_NOT_NULL_OR_UNDEFINED(this.data.searchTruckForm) &&
			IS_NOT_NULL_OR_UNDEFINED(this.data.searchTruckForm.origin.city) &&
			IS_NOT_NULL_OR_UNDEFINED(this.data.searchTruckForm.destination.city)
		);
	}

	formatType(type: string): string {
		return PROJECT_TYPES[type] ? PROJECT_TYPES[type].label : '';
	}

	getCreateTitle(): string {
		// Return 'Create Workflow' for workflows page, 'Create Project' for all others
		return this.data.projectSource === 'workflows_page' ? 'Create Workflow' : 'Create Project';
	}

	laneIsRequired(): boolean {
		const hasInvalidLaneInput =
			this.projectDetails.controls['destCity'].invalid ||
			this.projectDetails.controls['originCity'].invalid ||
			this.projectDetails.controls['equipmentType'].invalid;
		const hasValueInAnyLaneInput =
			IS_NOT_EMPTY(this.projectDetails.get('originCity').value) ||
			IS_NOT_EMPTY(this.projectDetails.get('originState').value) ||
			IS_NOT_EMPTY(this.projectDetails.get('destCity').value) ||
			IS_NOT_EMPTY(this.projectDetails.get('destState').value) ||
			IS_NOT_EMPTY(this.projectDetails.get('equipmentType').value);
		return hasInvalidLaneInput && hasValueInAnyLaneInput;
	}

	async getEquipment() {
		if (
			IS_NOT_NULL_OR_UNDEFINED(this.data.scorecardStartDate) &&
			IS_NOT_NULL_OR_UNDEFINED(this.data.scorecardEndDate) &&
			IS_NOT_NULL_OR_UNDEFINED(this.data.scorecardLoadsCount)
		) {
			const equipmentResponse = await this.projectUtilService.getEquipmentForScorecardLane({
				originCity: this.data.originCity,
				originState: this.data.originState,
				destinationCity: this.data.destCity,
				destinationState: this.data.destState,
				scorecardStartDate: this.data.scorecardStartDate,
				scorecardEndDate: this.data.scorecardEndDate,
				scorecardLoadsCount: this.data.scorecardLoadsCount,
				carrierId: this.data.carrierId,
				customerId: this.data.customerId,
			});
			this.trailerType = equipmentResponse.data;
			this.projectDetails.get('equipmentType').setValue(this.trailerType);
		}
	}

	inviteUngroupedCarriersUpdated(event: MatCheckboxChange) {
		if (!event.source || event.source.name != 'invite-ungrouped-carriers') {
			return;
		}
		if (event.checked) {
			this.projectDetails.get('ungroupedCarriersDelay').enable();
		} else {
			this.projectDetails.get('ungroupedCarriersDelay').disable();
		}
	}

	onOutOfNetworkUpdate(event: MatCheckboxChange) {
		if (event.checked) {
			this.projectDetails.get('sendToOutOfNetworkCarriersDelay').enable();
			this.projectDetails
				.get('sendToOutOfNetworkCarriersDelay')
				.setValidators([Validators.required, Validators.min(0)]);
		} else if (event.checked === false) {
			this.projectDetails.get('sendToOutOfNetworkCarriersDelay').disable();
			this.projectDetails.get('sendToOutOfNetworkCarriersDelay').setValidators([]);
		}
	}

	async setOutOfNetworkFormField() {
		await this.baConfigService.setBaConfig();
		const baConfig = this.baConfigService.getBAConfig();
		// Only set default delay value for new projects (edit/duplicate already has values from updateEditProjectForm)
		const currentDelay = this.projectDetails.get('sendToOutOfNetworkCarriersDelay').value;
		if (IS_NOT_EMPTY(baConfig?.data) && currentDelay === null) {
			const bookingAutomationConfigurationData = JSON.parse(baConfig.data);
			this.projectDetails
				.get('sendToOutOfNetworkCarriersDelay')
				.setValue(bookingAutomationConfigurationData.outOfNetworkDelay);
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}

export interface ProjectDialogInputData {
	isEditProject: boolean;
	addProject: boolean;
	searchTruckForm: any;
	truckLaneSearchId: string;
	truckSearchId: string;
	carrierIDs: Array<string>;
	projectDTO: any;
	isDuplicateProject: boolean;
	lanePricing: any;
	addProjectLink: boolean;
	projectSource: string;
	usersList: Array<Owner>;
	addAllCarriers: boolean;
	forScorecardAction: boolean;
	selectedCustomer: CustomerDTO;
	laneId: string;
	originCity: string;
	originState: string;
	destCity: string;
	destState: string;
	loadVolume: number;
	loadVolumeDuration: string;
	type?: string;
	scorecardStartDate: string;
	scorecardEndDate: string;
	scorecardLoadsCount: number;
	carrierId: string;
	customerId: string;
	notifyProcuredCarriers?: boolean;
	allowedTypes?: string[];
	refreshAction?: any; // Action to dispatch after notify dialog closes
	showProjectTypeSelectionDialog?: boolean;
}
